import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { CustomDataTable } from "@/components/table/CustomDataTable";
import { TableLoadingOverlay } from "@/components/ui/table-loading-overlay";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, XCircle, ExternalLink, Receipt, CornerLeftDown } from "lucide-react";
import { OnboardingForm } from "@/types/onboarding";
import { format, parseISO } from "date-fns";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MultiSelect } from "@/components/ui/multi-select";
import { CreatorInfo } from "@/components/kol/CreatorInfo";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { PicSelectorDialog } from './PicSelectorDialog';
import { useOnboardingPhases } from './hooks/useOnboardingPhases';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { getCurrentPhase } from '@/utils/onboardingUtils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "@/hooks/use-toast";

interface OnboardingFormsTableProps {
  forms: OnboardingForm[];
  isLoading: boolean;
  onApprove: (formId: string) => void;
  onDecline: (formId: string) => void;
  searchQuery: string;
  statusFilter: string;
  onUpdateForm: (formId: string, updatedData: Partial<OnboardingForm>) => Promise<boolean>;
  picOptions: {value: string, label: string}[];
  isUserRegionalManager: boolean;
}

export const OnboardingFormsTable: React.FC<OnboardingFormsTableProps> = ({ 
  forms, 
  isLoading, 
  onApprove, 
  onDecline,
  searchQuery,
  statusFilter,
  onUpdateForm,
  picOptions,
  isUserRegionalManager
}) => {
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [filteredForms, setFilteredForms] = useState<OnboardingForm[]>(forms);
  const [isProcessingUpdate, setIsProcessingUpdate] = useState(false);
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [selectedInvoiceUrl, setSelectedInvoiceUrl] = useState<string | null>(null);
  const [picDialogOpen, setPicDialogOpen] = useState(false);
  const [selectedForm, setSelectedForm] = useState<OnboardingForm | null>(null);
  const [sendBackDialogOpen, setSendBackDialogOpen] = useState(false);
  const [formToSendBack, setFormToSendBack] = useState<string | null>(null);
  const { updatePhaseStatus } = useOnboardingPhases();

  // Add this function to format collaboration period
  const formatCollaborationPeriod = (period?: string) => {
    if (!period) return '';
    
    // Format the period for display
    switch (period) {
      case 'less_than_month': return 'Less than a month';
      case '1_month': return '1 month';
      case '3_months': return '3 months';
      case '6_months': return '6 months';
      case 'more_than_year': return 'More than a year';
      case 'custom': return 'Custom';
      default: return period.replace(/_/g, ' ');
    }
  };

  // Helper function to determine the current phase status text
  const getPhaseStatusText = (form: OnboardingForm) => {
    const currentPhase = getCurrentPhase(form);
    
    let statusText = "";
    let phaseStatusKey = `status_phase_${currentPhase}` as keyof OnboardingForm;
    let phaseStatus = form[phaseStatusKey] as string | undefined;
    
    switch (currentPhase) {
      case 1:
        statusText = "Form Submission";
        break;
      case 2:
        statusText = "Pending Approval";
        break;
      case 3:
        statusText = "Invoice Generation";
        break;
      case 4:
        statusText = "Test Transaction";
        break;
      case 5:
        statusText = "Test Confirmation";
        break;
      case 6:
        statusText = "Final Payment";
        break;
      case 7:
        statusText = "Completed";
        break;
      default:
        statusText = "Unknown Phase";
    }
    
    return `Phase ${currentPhase}: ${statusText}`;
  };

  // Filter forms based on search query and status filter
  useEffect(() => {
    const filtered = forms.filter(form => {
      const matchesSearch = !searchQuery || 
        form.kol_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        form.social_media_handler?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        form.social_media_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        form.region?.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesStatus = statusFilter === 'all' || form.approved === statusFilter;
      
      return matchesSearch && matchesStatus;
    });
    
    setFilteredForms(filtered);
  }, [forms, searchQuery, statusFilter]);

  // Function to handle updating a form field
  const handleFormUpdate = async (formId: string, field: string, value: any) => {
    setIsProcessingUpdate(true);
    try {
      const success = await onUpdateForm(formId, { [field]: value });
      if (!success) throw new Error("Update failed");
      return true;
    } catch (error) {
      console.error("Error updating form:", error);
      return false;
    } finally {
      setIsProcessingUpdate(false);
    }
  };

  // Function to handle updating the PIC field
  const handlePicChange = async (formId: string, selectedPics: string[]) => {
    return handleFormUpdate(formId, 'pic', selectedPics);
  };

  // Function to handle test_transfer toggle
  const handleTestTransferChange = async (formId: string, value: boolean) => {
    return handleFormUpdate(formId, 'test_transfer', value);
  };
  
  // Function to handle payment_token selection
  const handlePaymentTokenChange = async (formId: string, value: string) => {
    return handleFormUpdate(formId, 'payment_token', value);
  };

  // Function to open invoice dialog
  const handleOpenInvoiceDialog = (invoiceUrl: string) => {
    setSelectedInvoiceUrl(invoiceUrl);
    setInvoiceDialogOpen(true);
  };

  // Function to handle form approval with PIC assignment
  const handleApproveForm = (form: OnboardingForm) => {
    setSelectedForm(form);
    setPicDialogOpen(true);
  };

  // Function to handle PIC selection and form approval
  const handlePicSave = async (picArray: string[], campaignBU: string) => {
    if (selectedForm) {
      setPicDialogOpen(false);
      
      // Update both pic and campaign_bu fields
      const updateData: Partial<OnboardingForm> = {
        pic: picArray,
        campaign_bu: campaignBU,
        approved: 'approved',
        status_phase_2: 'completed',
        status_phase_3: 'in-progress'
      };
      
      // Update the form data
      const success = await onUpdateForm(selectedForm.id, updateData);
      
      if (success) {
        // Complete the approval
        onApprove(selectedForm.id);
      }
      
      setSelectedForm(null);
    }
  };

  // Function to handle sending back a form
  const handleSendBack = (formId: string) => {
    setFormToSendBack(formId);
    setSendBackDialogOpen(true);
  };

  // Function to confirm sending back a form
  const confirmSendBack = async () => {
    if (formToSendBack) {
      setIsProcessingUpdate(true);
      try {
        // Update form data with all required phase resets and clear specific fields
        await onUpdateForm(formToSendBack, {
          approved: 'pending',
          status_phase_1: 'in-progress',
          status_phase_2: 'pending',
          status_phase_3: 'pending',
          status_phase_4: 'pending',
          status_phase_5: 'pending',
          status_phase_6: 'pending',
          status_phase_7: 'pending',
          // Clear these specific fields when sending back
          test_transfer_url: null,
          tx_hash: null,
          tx_hash_balance: null, // Also clear the balance transfer TX hash
          invoice_link: null,
          // Also reset test transfer status
          test_transfer: false
        });
        
        setSendBackDialogOpen(false);
        setFormToSendBack(null);
        
        toast({
          title: "Form Reset Successfully",
          description: "The form has been sent back to Phase 1 and relevant data has been cleared"
        });
      } catch (error) {
        console.error('Error sending form back:', error);
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to send the form back. Please try again."
        });
      } finally {
        setIsProcessingUpdate(false);
      }
    }
  };

  // Define columns for the data table
  const columns = [
    {
      id: 'created_at',
      header: 'Created',
      accessorKey: 'created_at' as string,
      sortable: true,
      width: 120,
      freezeLeft: true,
      cell: (row: any) => {
        return format(parseISO(row.created_at), 'yyyy-MM-dd');
      }
    },
    {
      id: 'kol_name',
      header: 'Creator Info',
      accessorKey: 'kol_name' as string,
      sortable: true,
      editable: true,
      minWidth: 200,
      freezeLeft: true,
      cell: (row: any) => {
        return (
          <CreatorInfo
            name={row.kol_name ? row.kol_name.toUpperCase() : ''}
            handler={row.social_media_handler ? row.social_media_handler.toUpperCase() : ''}
            platform={row.social_media_type}
            region={row.region ? row.region.toUpperCase() : ''}
            profileImageUrl={null}
            compact={true}
            hideBadges={false}
          />
        );
      }
    },
    {
      id: 'email',
      header: 'Email',
      accessorKey: 'email' as string,
      sortable: true,
      editable: true,
      minWidth: 180,
      freezeLeft: true,
      cell: (row: any) => {
        return row.email || <span className="text-gray-500 text-xs">Not provided</span>;
      }
    },
    {
      id: 'description',
      header: 'Description',
      accessorKey: 'description' as string,
      sortable: true,
      editable: true,
      minWidth: 250,
      freezeLeft: true,
      cell: (row: any) => {
        return row.description ? (
          <div className="text-sm text-gray-600 whitespace-normal py-2 max-w-[350px] text-xs">
            {row.description}
          </div>
        ) : null;
      }
    },
    {
      id: 'content_links',
      header: 'Content Links',
      accessorKey: 'content_links' as string,
      sortable: false,
      editable: false,
      minWidth: 250,
      freezeLeft: true,
      cell: (row: any) => {
        const links = Array.isArray(row.content_links) ? row.content_links : [];
        return links.length > 0 ? (
          <div className="flex flex-col space-y-2 py-1">
            {links.map((link: any, index: number) => {
              const url = typeof link === 'string' ? link : link.url;
              const displayUrl = url;
              return (
                <a 
                  key={index} 
                  href={url} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="text-blue-500 hover:underline flex items-center group"
                >
                  <span className="text-xs break-all">{displayUrl}</span>
                  <ExternalLink className="h-3 w-3 ml-1 opacity-0 group-hover:opacity-100 transition-opacity" />
                </a>
              );
            })}
          </div>
        ) : (
          <span className="text-gray-500 text-xs">No links</span>
        );
      }
    },
    {
      id: 'collaboration_period',
      header: 'Collaboration Period',
      accessorKey: 'collaboration_period' as string,
      sortable: true,
      editable: true,
      inputType: "select" as const,
      selectOptions: [
        { label: "Less than a month", value: "less_than_month" },
        { label: "1 month", value: "1_month" },
        { label: "3 months", value: "3_months" },
        { label: "6 months", value: "6_months" },
        { label: "More than a year", value: "more_than_year" },
        { label: "Custom", value: "custom" }
      ],
      minWidth: 200,
      cell: (row: any) => {
        if (row.start_date && row.end_date) {
          return `${format(new Date(row.start_date), "MMM d, yyyy")} to ${format(new Date(row.end_date), "MMM d, yyyy")}`;
        } else if (row.collaboration_period) {
          // Format the period for display
          return formatCollaborationPeriod(row.collaboration_period);
        } else {
          return <span className="text-gray-500 text-xs">Not specified</span>;
        }
      }
    },
    {
      id: 'agreed_rate',
      header: 'Agreed Rate',
      accessorKey: 'agreed_rate' as string,
      sortable: true,
      editable: true,
      inputType: "number" as const,
      width: 150,
      freezeLeft: true,
      cell: (row: any) => {
        return row.agreed_rate ? `${row.agreed_rate} ${row.currency_type || ''}` : null;
      }
    },
    {
      id: 'pic',
      header: 'PIC',
      accessorKey: 'pic' as string,
      sortable: false,
      editable: isUserRegionalManager,
      width: 200,
      freezeLeft: true,
      cell: (row: any) => {
        const picArray = Array.isArray(row.pic) ? row.pic : [];
        
        if (!isUserRegionalManager) {
          return (
            <div className="flex flex-wrap gap-1 py-1">
              {picArray.length > 0 ? picArray.map((pic, index) => (
                <Badge key={index} variant="outline">{pic}</Badge>
              )) : (
                <span className="text-gray-500 text-xs">No PIC assigned</span>
              )}
            </div>
          );
        }
        
        return (
          <MultiSelect
            options={picOptions}
            selected={picArray}
            onChange={(selected) => handlePicChange(row.id, selected)}
            placeholder="Select PICs"
            maxDisplay={2}
            className="w-full"
          />
        );
      }
    },
    {
      id: 'experience_feedback',
      header: 'Experience & Feedback',
      minWidth: 220,
      width: 280,
      freezeLeft: true,
      editable: false,
      cell: (row: any) => {
        return (
          <div className="space-y-2">
            {row.experience_rate && (
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <span 
                    key={i}
                    className={`text-lg ${i < row.experience_rate ? 'text-yellow-500' : 'text-gray-300'}`}
                  >
                    ★
                  </span>
                ))}
              </div>
            )}
            {row.feedback && (
              <div className="text-xs text-gray-600 whitespace-normal max-w-[250px]">
                {row.feedback}
              </div>
            )}
          </div>
        );
      }
    },
    {
      id: 'invoice_link',
      header: 'Invoice',
      accessorKey: 'invoice_link' as string,
      sortable: false,
      editable: true,
      width: 100,
      freezeLeft: true,
      cell: (row: any) => {
        return row.invoice_link ? (
          <Button 
            variant="ghost" 
            size="sm" 
            className="h-8 px-2 flex items-center" 
            onClick={() => handleOpenInvoiceDialog(row.invoice_link)}
          >
            <Receipt className="h-4 w-4 mr-1" />
            <span className="text-xs">View</span>
          </Button>
        ) : (
          <span className="text-gray-500 text-xs">No invoice</span>
        );
      }
    },
    {
      id: 'approved',
      header: 'Status',
      accessorKey: 'approved' as string,
      sortable: true,
      width: 560,
      editable: false,
      cell: (row: any) => {
        // New status display that shows the current phase and its status
        const phaseStatusText = getPhaseStatusText(row);
        
        switch (row.approved) {
          case 'approved':
            return (
              <Badge className="bg-green-100 text-green-800 flex items-center gap-1 whitespace-nowrap">
                <CheckCircle className="h-3 w-3" /> {phaseStatusText}
              </Badge>
            );
          case 'declined':
            return (
              <Badge className="bg-red-100 text-red-800 flex items-center gap-1 whitespace-nowrap">
                <XCircle className="h-3 w-3" /> Declined
              </Badge>
            );
          default:
            return (
              <Badge className="bg-yellow-100 text-yellow-800 flex items-center gap-1 whitespace-nowrap">
                <span className="h-2 w-2 rounded-full bg-yellow-500 inline-block" /> {phaseStatusText}
              </Badge>
            );
        }
      }
    },
    {
      id: 'payment_status',
      header: 'Payment Status',
      accessorKey: 'payment_status',
      sortable: true,
      width: 140,
      editable: false,
      cell: (row: any) => {
        switch (row.payment_status) {
          case 'completed':
            return <Badge className="bg-green-100 text-green-800">Completed</Badge>;
          case 'pending':
            return <Badge className="bg-yellow-100 text-yellow-800">Pending</Badge>;
          case 'rejected':
            return <Badge className="bg-red-100 text-red-800">Rejected</Badge>;
          default:
            return <Badge className="bg-gray-100 text-gray-800">Not set</Badge>;
        }
      }
    },
    {
      id: 'finance_comments',
      header: 'Finance Comments',
      accessorKey: 'finance_comments',
      sortable: true,
      editable: true,
      minWidth: 200,
      cell: (row: any) => {
        return row.finance_comments ? (
          <div className="text-sm text-gray-600 whitespace-normal py-1">{row.finance_comments}</div>
        ) : (
          <span className="text-gray-500 text-xs">No comments</span>
        );
      }
    },
    {
      id: 'test_transfer',
      header: 'Got test transfer?',
      accessorKey: 'test_transfer',
      sortable: true,
      editable: true,
      width: 140,
      cell: (row: any) => {
        return (
          <Select
            value={row.test_transfer ? "true" : "false"}
            onValueChange={(value) => handleTestTransferChange(row.id, value === "true")}
          >
            <SelectTrigger className="h-8 text-xs w-[100px]">
              <SelectValue>
                {row.test_transfer ? "Done" : "Not yet"}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="false">Not yet</SelectItem>
              <SelectItem value="true">Done</SelectItem>
            </SelectContent>
          </Select>
        );
      }
    },
    {
      id: 'test_transfer_url',
      header: 'Test Transfer URL',
      accessorKey: 'test_transfer_url',
      sortable: true,
      editable: true,
      width: 540,
      cell: (row: any) => {
        return row.test_transfer_url ? (
          <div className="text-xs font-mono truncate max-w-[500px]">
            <a 
              href={row.test_transfer_url} 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-blue-500 hover:underline flex items-center group"
            >
              <span className="text-xs truncate">{row.test_transfer_url}</span>
              <ExternalLink className="h-3 w-3 ml-1 opacity-0 group-hover:opacity-100 transition-opacity" />
            </a>
          </div>
        ) : (
          <span className="text-gray-500 text-xs">No URL</span>
        );
      }
    },
    {
      id: 'tx_hash',
      header: 'TX Hash',
      accessorKey: 'tx_hash',
      sortable: true,
      editable: true,
      width: 540,
      cell: (row: any) => {
        return row.tx_hash ? (
          <div className="text-xs font-mono truncate max-w-[500px]">
            <a 
              href={`https://explorer.mantle.xyz/tx/${row.tx_hash}`} 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-blue-500 hover:underline flex items-center group"
            >
              <span className="text-xs truncate">{row.tx_hash}</span>
              <ExternalLink className="h-3 w-3 ml-1 opacity-0 group-hover:opacity-100 transition-opacity" />
            </a>
          </div>
        ) : (
          <span className="text-gray-500 text-xs">No hash</span>
        );
      }
    },
    {
      id: 'tx_hash_balance',
      header: 'Balance Transfer TX Hash',
      accessorKey: 'tx_hash_balance',
      sortable: true,
      editable: true,
      width: 200,
      cell: (row: any) => {
        return row.tx_hash_balance ? (
          <div className="text-xs font-mono truncate max-w-[150px]">
            <a 
              href={`https://explorer.mantle.xyz/tx/${row.tx_hash_balance}`} 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-blue-500 hover:underline flex items-center group"
            >
              <span className="text-xs truncate">{row.tx_hash_balance}</span>
              <ExternalLink className="h-3 w-3 ml-1 opacity-0 group-hover:opacity-100 transition-opacity" />
            </a>
          </div>
        ) : (
          <span className="text-gray-500 text-xs">No hash</span>
        );
      }
    },
    {
      id: 'payment_amount',
      header: 'Payment Amount',
      accessorKey: 'payment_amount' as string,
      sortable: true,
      editable: true,
      inputType: "number" as const,
      width: 180, // Increased width from 140 to 180
      cell: (row: any) => {
        return row.payment_amount !== null && row.payment_amount !== undefined ? 
          row.payment_amount : 
          <span className="text-gray-500 text-xs">Not set</span>;
      }
    },
    {
      id: 'payment_token',
      header: 'Payment Token',
      accessorKey: 'payment_token',
      sortable: true,
      editable: true,
      width: 150, // Increased width from 130 to 150
      cell: (row: any) => {
        return (
          <Select
            value={row.payment_token || ""}
            onValueChange={(value) => handlePaymentTokenChange(row.id, value)}
          >
            <SelectTrigger className="h-8 text-xs w-[120px]">
              <SelectValue>
                {row.payment_token || "Select token"}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="$USDC">$USDC</SelectItem>
              <SelectItem value="$USDT">$USDT</SelectItem>
              <SelectItem value="$MNT">$MNT</SelectItem>
            </SelectContent>
          </Select>
        );
      }
    },
    {
      id: 'actions',
      header: 'Actions',
      width: 120,
      editable: false,
      cell: (row: any) => {
        if (row.approved === 'approved' || row.approved === 'declined') {
          return (
            <div className="w-full flex justify-center">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="h-8 w-8 p-0" 
                      onClick={() => handleSendBack(row.id)}
                    >
                      <CornerLeftDown className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Send Back</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          );
        }
        
        return (
          <div className="w-full flex justify-center gap-2">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="h-8 w-8 p-0 border-green-500 text-green-600 hover:bg-green-50 hover:text-green-700" 
                    onClick={() => handleApproveForm(row)}
                  >
                    <CheckCircle className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Approve</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="h-8 w-8 p-0 border-red-500 text-red-600 hover:bg-red-50 hover:text-red-700" 
                    onClick={() => onDecline(row.id)}
                  >
                    <XCircle className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Decline</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="h-8 w-8 p-0 border-blue-500 text-blue-600 hover:bg-blue-50 hover:text-blue-700" 
                    onClick={() => handleSendBack(row.id)}
                  >
                    <CornerLeftDown className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Send Back</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        );
      }
    }
  ];

  return (
    <>
      {isProcessingUpdate && (
        <TableLoadingOverlay isLoading={true} message="Updating form..." />
      )}
      <CustomDataTable
        data={filteredForms}
        columns={columns}
        loading={isLoading}
        enableRowSelection={true}
        selectedRows={selectedRows}
        onRowSelectionChange={setSelectedRows}
        enableSorting={true}
        onRowEdit={(newRow, oldRow) => {
          const fieldsThatChanged = Object.keys(newRow).filter(key => 
            newRow[key] !== oldRow[key] && 
            key !== 'id' && 
            !key.startsWith('_')
          );
          
          if (fieldsThatChanged.length === 0) return Promise.resolve(newRow);
          
          const updateData: any = {};
          fieldsThatChanged.forEach(field => {
            updateData[field] = newRow[field];
            
            // Update phase status when test_transfer_url is modified
            if (field === 'test_transfer_url' && newRow.test_transfer_url) {
              // Update phase 4 status when test_transfer_url is provided
              updateData.status_phase_4 = 'completed';
              updateData.status_phase_5 = 'in-progress';
            }
          });
          
          return onUpdateForm(newRow.id, updateData).then(success => {
            if (success) return newRow;
            return oldRow;
          });
        }}
      />

      {/* Invoice Dialog */}
      <Dialog open={invoiceDialogOpen} onOpenChange={setInvoiceDialogOpen}>
        <DialogContent className="w-full max-w-4xl max-h-[80vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>Invoice</DialogTitle>
          </DialogHeader>
          {selectedInvoiceUrl && (
            <div className="mt-4">
              <iframe 
                src={selectedInvoiceUrl} 
                className="w-full h-[70vh] border-0" 
                title="Invoice Preview"
              />
              <div className="mt-4 flex justify-between">
                <Button variant="outline" onClick={() => setInvoiceDialogOpen(false)}>
                  Close
                </Button>
                <Button 
                  variant="default" 
                  onClick={() => window.open(selectedInvoiceUrl, '_blank')}
                >
                  Open in New Tab
                  <ExternalLink className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* PIC Selector Dialog */}
      {selectedForm && (
        <PicSelectorDialog
          open={picDialogOpen}
          onClose={() => setPicDialogOpen(false)}
          form={selectedForm}
          onSave={handlePicSave}
        />
      )}

      {/* Send Back Confirmation Dialog */}
      <AlertDialog open={sendBackDialogOpen} onOpenChange={setSendBackDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Send Back Form?</AlertDialogTitle>
            <AlertDialogDescription>
              This will reset the form to Phase 1 and clear all progress, including test transfer URL, transaction hashes, and invoice link data. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmSendBack}
              className="bg-yellow-500 hover:bg-yellow-600"
              disabled={isProcessingUpdate}
            >
              {isProcessingUpdate ? "Processing..." : "Send Back"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default OnboardingFormsTable;
